import { Box, Button, EditIcon, LoadingButton, Section, Stack } from '@cmg/design-system';
import { FormikProvider, useFormik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';

import type { XcEmailSubscriptions } from '../../../../../types/domain/self/profile';
import type { XcNotificationSettingPartsFragment } from './graphql/__generated__';
import { XcEmailCategorySections } from './XcEmailCategorySections';
import { groupNotificationsByCategory } from './XcEmailSubscriptions.model';

export type XcEmailSubscriptionsGridSectionProps = {
  initialValues: XcEmailSubscriptions;
  xcNotifications: readonly XcNotificationSettingPartsFragment[];
  loading: boolean;
  onSubmit: (values: XcEmailSubscriptions) => Promise<void> | void;
  canEdit?: boolean;
};

export const XcEmailSubscriptionsGridSection: React.FC<XcEmailSubscriptionsGridSectionProps> = ({
  initialValues,
  xcNotifications,
  loading,
  onSubmit,
  canEdit,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const formik = useFormik<XcEmailSubscriptions>({
    initialValues,
    onSubmit: async (values, helpers) => {
      await onSubmit(values);
      helpers.resetForm({ values });
      setIsEditing(false);
    },
  });

  const disabledInEdit = loading || !isEditing;
  const handleCancel = useCallback(() => {
    formik.resetForm();
    setIsEditing(false);
  }, [formik]);

  const getActions = () => {
    if (!canEdit) {
      return undefined;
    }

    return isEditing
      ? [
          <Button
            key="subscription-cancel"
            variant="outlined"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>,
          <LoadingButton
            key="subscription-save"
            variant="contained"
            loading={loading}
            disabled={loading}
            onClick={() => formik.handleSubmit()}
          >
            Save
          </LoadingButton>,
        ]
      : [
          <Button
            key="subscription-edit"
            variant="text"
            startIcon={<EditIcon />}
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>,
        ];
  };

  const notificationsByCategory = useMemo(
    () => groupNotificationsByCategory(xcNotifications ?? []),
    [xcNotifications]
  );

  return (
    <FormikProvider value={formik}>
      <Section
        title="XC Email Subscriptions"
        actions={getActions()}
        data-testid="xc-email-subscriptions-section"
        mt={3}
        pl={3}
        sx={{ width: '100%' }}
      >
        <Stack gap={2} direction={{ sm: 'column', md: 'row' }}>
          <Stack gap={2} direction={{ md: 'column', lg: 'row' }} flex={{ md: 1, lg: 2 }}>
            <Box flex={1}>
              <XcEmailCategorySections
                categories={['Offering', 'Order Book']}
                notificationsByCategory={notificationsByCategory}
                isEditing={isEditing}
                disabled={disabledInEdit}
              />
            </Box>
            <Box flex={1}>
              <XcEmailCategorySections
                categories={['Indication']}
                notificationsByCategory={notificationsByCategory}
                isEditing={isEditing}
                disabled={disabledInEdit}
              />
            </Box>
          </Stack>
          <Box flex={1}>
            <XcEmailCategorySections
              categories={['Retail', 'Allocation']}
              notificationsByCategory={notificationsByCategory}
              isEditing={isEditing}
              disabled={disabledInEdit}
            />
          </Box>
        </Stack>
      </Section>
    </FormikProvider>
  );
};
